import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PhygitalIcon from '@/components/PhygitalIcon';
import { toast } from 'sonner';
import { ArrowRight, Check, Calendar } from 'lucide-react';
import { useUiSurface, slotText } from '@/hooks/useUiSurface';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventWaitlistPage = () => {
  const { slug } = useParams();
  // Iter 9.47.k - copy sourced from Airtable `surface_waitlist`.
  const { slotMap: w } = useUiSurface('surface_waitlist');
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [joined, setJoined] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchEvent();
  }, [slug]);

  const fetchEvent = async () => {
    try {
      const res = await fetch(`${API}/events/${slug}`);
      if (res.ok) {
        const data = await res.json();
        setEvent(data);
      }
    } catch (err) {
      console.error('Error fetching event:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinWaitlist = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/events/${event?.event_id}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (res.ok) {
        setJoined(true);
        toast.success("You're on the list!");
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Failed to join waitlist');
    } finally {
      setSubmitting(false);
    }
  };

  // Get cover image from event - always syncs with event data
  const getImageUrl = () => {
    const img = event?.cover_image_url || event?.cover_image;
    if (!img) return null;
    if (img.startsWith('/')) {
      return `${API}${img}`;
    }
    return img;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse text-gray-400" data-testid="waitlist-loading">{slotText(w, 'loading_label', 'Loading...')}</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-xl p-6 text-center shadow-sm max-w-sm">
          <h1 className="text-lg font-bold mb-2" data-testid="waitlist-not-found-title">{slotText(w, 'not_found_title', 'Experience Not Found')}</h1>
          <p className="text-gray-500 text-sm mb-4" data-testid="waitlist-not-found-body">{slotText(w, 'not_found_body', "This experience doesn't exist or has been removed.")}</p>
          <Link to="/">
            <Button size="sm" data-testid="waitlist-go-home-btn">{slotText(w, 'go_home_btn', 'Go Home')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const imageUrl = getImageUrl();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Compact Header */}
      <header className="bg-white border-b py-3 px-4">
        <Link to="/" className="flex items-center gap-2">
          <PhygitalIcon className="w-6 h-6" variant="dark" />
          <span className="font-semibold text-sm">phygital</span>
        </Link>
      </header>

      {/* Main Content - Centered and Compact */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm w-full max-w-sm p-5">
          {!joined ? (
            <>
              {/* Event Cover Image - synced from event, with padding like everywhere else */}
              <div className="flex justify-center mb-4">
                {imageUrl ? (
                  <div className="w-28 h-28 rounded-lg bg-black p-3 overflow-hidden">
                    <img 
                      src={imageUrl}
                      alt={event.name}
                      className="w-full h-full object-contain"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                ) : (
                  <div className="w-28 h-28 rounded-lg bg-black flex items-center justify-center text-white font-bold text-2xl">
                    {event.name?.split(' ').map(w => w[0]).join('').slice(0, 4)}
                  </div>
                )}
              </div>

              {/* Event Info */}
              <div className="text-center mb-4">
                <h1 className="text-xl font-bold text-gray-900">{event.name}</h1>
                {event.date && (
                  <p className="text-gray-500 text-sm flex items-center justify-center gap-1.5 mt-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(event.date + 'T00:00:00').toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                )}
              </div>

              {/* RSVP Opening Soon Text */}
              <div className="text-center mb-4">
                <p className="text-sm font-medium text-gray-700" data-testid="waitlist-rsvp-opening">{slotText(w, 'rsvp_opening_label', 'RSVP Opening Soon!')}</p>
              </div>

              {/* Email Form */}
              <form onSubmit={handleJoinWaitlist} className="space-y-3">
                <Input
                  type="email"
                  placeholder={slotText(w, 'email_placeholder', 'you@example.com')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full"
                  required
                  data-testid="waitlist-email-input"
                />
                <Button 
                  type="submit" 
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  disabled={submitting}
                  data-testid="waitlist-notify-btn"
                >
                  {submitting ? slotText(w, 'joining_label', 'Joining...') : slotText(w, 'notify_btn', 'Notify Me')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </>
          ) : (
            /* Success State */
            <div className="text-center py-4">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-lg font-bold mb-2" data-testid="waitlist-joined-title">{slotText(w, 'joined_title', "You're on the list!")}</h2>
              <p className="text-gray-600 text-sm" data-testid="waitlist-joined-body">
                {(() => {
                  const tmpl = slotText(w, 'joined_body', "We'll email you at {email} when RSVP opens.");
                  const parts = tmpl.split('{email}');
                  return (
                    <>
                      {parts[0]}
                      <strong>{email}</strong>
                      {parts[1] ?? ''}
                    </>
                  );
                })()}
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default EventWaitlistPage;
